import {DnsRecord} from '../types';
import {
    validateRecordName,
    validateRecordValues,
    validateTtl,
} from './recordValidation';

// FNP-1192 — the form's own value shape, and the validation that runs over it.
//
// Kept apart from the component so the rules can be tested without rendering
// anything, and so a file that exports a component does not also export
// constants (react-refresh/only-export-components, and the repo lints at
// --max-warnings 0).

export type RecordFormValues = {
    name: string,
    type: string,
    ttlSeconds: number,
    values: string[],
    // ⚠️ THE PAIR THE EDIT STARTED FROM. A record's identity IS (name, type),
    // so an edit that changes either is a delete-plus-create rather than an
    // update — and the mutation needs to know the ORIGINAL pair to remove. For
    // a create these are empty strings and nothing reads them.
    originalName: string,
    originalType: string,
}

// A sensible starting point for a new record. One hour is the value most
// providers default to and is short enough that a mistake is recoverable
// without a long wait.
export const DEFAULT_TTL_SECONDS = 3600;

export const emptyRecordFormValues = (defaultType: string): RecordFormValues => ({
    name: '',
    type: defaultType,
    ttlSeconds: DEFAULT_TTL_SECONDS,
    values: [''],
    originalName: '',
    originalType: '',
});

// ⚠️ A POINTER'S `values` HOLDS THE RESOLVED TARGET, NOT ITS RDATA, so this is
// only ever called for a record that `isEditableAsRdata` allowed. Pre-filling
// an edit form from a pointer would offer the operator today's resolved address
// as if it were the record's stored value — and saving it would silently
// convert an alias into a literal record.
export const recordToFormValues = (record: DnsRecord): RecordFormValues => ({
    name: record.name,
    type: record.type,
    ttlSeconds: record.ttlSeconds ?? DEFAULT_TTL_SECONDS,
    values: record.values.length > 0 ? [...record.values] : [''],
    originalName: record.name,
    originalType: record.type,
});

/**
 * Validate the whole form.
 *
 * Returns a map keyed by field, with a `values.<index>` entry per bad value so
 * the component can mark the exact row. An empty map means the form may be
 * submitted.
 */
export const validateRecordForm = (values: RecordFormValues): Record<string, string> => {

    const errors: Record<string, string> = {};

    const nameError = validateRecordName(values.name);
    if (nameError) {
        errors.name = nameError;
    }

    if (!values.type) {
        errors.type = 'A record type is required.';
    }

    const ttlError = validateTtl(values.ttlSeconds);
    if (ttlError) {
        errors.ttlSeconds = ttlError;
    }

    // ⚠️ VALIDATED AGAINST THE CHOSEN TYPE, which is the criterion: an IPv4
    // address in a CNAME is caught here rather than coming back as a provider
    // rejection after a round trip.
    validateRecordValues(values.type, values.values)
        .forEach(problem => {
            errors[`values.${problem.index}`] = problem.message;
        });

    return errors;
};

/** Whether the (name, type) pair changed — i.e. whether this edit is a move. */
export const isMove = (values: RecordFormValues): boolean =>
    values.originalName !== '' && (
        values.name !== values.originalName || values.type !== values.originalType
    );
